import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { useProductTour } from './ProductTourProvider'
import { getOnPageTours, type TourDef } from './tours'

/**
 * How long to keep looking for this page's tour anchors after a route change.
 *
 * `getOnPageTours` reads the DOM, and a page's anchors do not all exist at first
 * paint — `certificates-table` only appears once the table has data. A single
 * post-render check therefore answers "no tours here" for every surface that
 * loads asynchronously, which is most of them.
 *
 * So it polls on a short, BOUNDED schedule and stops the moment it finds
 * something. Bounded because the alternative failure is worse than a missed
 * chip: an unbounded observer on `document.body` would run for the life of the
 * page on every surface, to answer a question that is settled within a second or
 * two of arrival.
 *
 * The last entry is deliberately past a typical list fetch. If a surface is
 * slower than this, its chip does not appear — a silently missing invitation,
 * never a wrong one.
 */
const ANCHOR_POLL_MS = [0, 250, 750, 1800, 3500]

/**
 * The tours that are about THIS page and that this user has not taken.
 *
 * The intersection of two independent questions, and both halves matter:
 *
 *  - RELEVANT — `getOnPageTours()` checks which `data-tour` anchors are actually
 *    in the DOM, so relevance needs no route→tour table and cannot drift from
 *    the UI (a deleted anchor is a build failure, per `check-tour-anchors.mjs`).
 *  - UNTAKEN — `hasSeenTour(id) === false`, checked STRICTLY against `false`.
 *    `undefined` means "not known yet", which is a different answer, and
 *    treating it as unseen is what makes an invitation flash on every cold load
 *    for someone who finished the tour months ago. `isTourProgressReady` gates
 *    the whole thing for the same reason.
 *
 * Returns `[]` while an answer is unavailable, which is the honest default: show
 * nothing until we know, rather than invite someone to something they have done.
 */
export const useOnPageUntakenTours = (): TourDef[] => {
  const { hasSeenTour, isTourProgressReady } = useProductTour()
  const { pathname } = useLocation()
  const [onPage, setOnPage] = useState<TourDef[]>([])

  useEffect(() => {
    // Re-ask on every navigation. Without the reset, a page with no tours would
    // keep showing the previous page's chip until its own poll completed.
    setOnPage([])

    const timers = ANCHOR_POLL_MS.map(delay =>
      window.setTimeout(() => {
        const found = getOnPageTours()
        if (found.length > 0) {
          setOnPage(found)
          timers.forEach(window.clearTimeout)
        }
      }, delay),
    )
    return () => timers.forEach(window.clearTimeout)
  }, [pathname])

  /*
    Registry order, unsorted — and that is a deliberate reversal.

    A "resumable first" sort was added here so the chip's singular pick would be
    principled rather than "whatever the registry listed first". Then the chip
    learned to hand 2+ tours to the library, and the sort became UNOBSERVABLE: the
    only consumer reads `[0]`, and `[0]` is only rendered when the list has exactly
    one element, which no ordering can change. Sorting a one-element list is not a
    policy, it is decoration.

    Removed rather than left in, because dead ordering is worse than none: the next
    reader would reasonably assume something depends on it and preserve it through a
    refactor. If a future consumer does need "most relevant first" — a plural chip
    that names the top tour, say — the rule to reinstate is resumable-first, ties on
    registry order, and it should arrive with that consumer.
  */
  return useMemo(() => {
    if (!isTourProgressReady) return []
    return onPage.filter(tour => hasSeenTour(tour.id) === false)
  }, [onPage, isTourProgressReady, hasSeenTour])
}
